import { Input, Select } from "antd";
import classNames from "classnames";
import { find, get, isEmpty, isNil, omit } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SourceEditor, { getSourceMode } from "shared/components/SourceEditor";
import { NAME_SPACES } from "shared/locales/constants";
import { hasSource, isStaticMapping } from "../../anchors";
import {
  CONDITION_OPERATORS,
  MAPPING_TYPES,
  RULE_OPERATORS,
  SOURCE_MODES,
  TRANSFORM_BY_OP,
} from "../../constants";
import {
  DEFAULT_DATE_FORMAT,
  FIELD_KINDS,
  fieldOptions,
  fieldPhrase,
  findFieldByPath,
  findFieldForMapping,
  readFieldValue,
} from "../../data-fields";
import { ZONE_TYPES } from "../../field-status";
import AdvancedOptions from "./advanced-options";
import FieldPicker from "./field-picker";
import {
  checkboxKey,
  conditionsPass,
  dateTransformOf,
  formatDate,
  LEAVE_EMPTY_MAPPING,
  listPathOf,
  mappingForField,
  readMappingValue,
  readSourceValue,
  tableColumnOf,
  valueText,
  withDateFormat,
  withoutKey,
  withSource,
  zoneLabel,
} from "./zones";

const MODES = {
  FIELD: "field",
  FIXED: "fixed",
  EMPTY: "empty",
  COMBINE: "combine",
};

const LIST_KINDS = [FIELD_KINDS.LIST];
const VALUE_KINDS = Object.values(FIELD_KINDS).filter(
  (kind) => kind !== FIELD_KINDS.LIST,
);
const CHECKBOX_KINDS = VALUE_KINDS.filter((kind) => kind !== FIELD_KINDS.DATE);
const BOOLEAN_VALUES = { YES: "true", NO: "false" };
const DATE_FORMS = TRANSFORM_BY_OP.date.fields[0].options;
const SOURCE_PARTS = ["key", "keys", "from"];

const asObject = (mapping) =>
  !isNil(mapping) && typeof mapping === "object" ? mapping : {};

const modeOf = (mapping, wantsFixed) => {
  if (isStaticMapping(mapping))
    return isEmpty(mapping.staticValue) && !wantsFixed
      ? MODES.EMPTY
      : MODES.FIXED;
  if (getSourceMode(asObject(mapping)) === SOURCE_MODES.COMBINE)
    return MODES.COMBINE;
  return MODES.FIELD;
};

const combined = (mapping) => {
  const part = Object.fromEntries(
    Object.entries(asObject(mapping)).filter(([key]) =>
      SOURCE_PARTS.includes(key),
    ),
  );
  return {
    ...withSource(mapping, {}),
    combine: { separator: " ", parts: isEmpty(part) ? [{ key: "" }] : [part] },
  };
};

const single = (mapping) => {
  const [first = {}] = get(mapping, "combine.parts") || [];
  const part = Object.fromEntries(
    Object.entries(first).filter(([key]) => SOURCE_PARTS.includes(key)),
  );
  return withSource(mapping, part);
};

const ruleText = (value) =>
  isNil(value) || value === "" || typeof value === "object"
    ? null
    : String(value);

const boxPlaceholder = ({ key, exists, rules }, FIELD_PANEL) => {
  if (isNil(key)) return FIELD_PANEL.BOX_PICK_FIRST;
  return exists && isEmpty(rules)
    ? FIELD_PANEL.BOX_ALWAYS
    : FIELD_PANEL.BOX_NEVER;
};

const isSimpleRule = (conditions, key) => {
  const rules = get(conditions, "rules") || [];
  if (isEmpty(rules)) return true;
  if (rules.length > 1) return false;
  const [rule] = rules;
  return (
    rule.op === RULE_OPERATORS.EQUALS && (isEmpty(rule.key) || rule.key === key)
  );
};

const Step = ({ title, children }) => (
  <section className="dynamic-documents-editor__step">
    {title && <h4 className="dynamic-documents-editor__step-title">{title}</h4>}
    {children}
  </section>
);

const LinkButton = ({ children, onClick }) => (
  <button
    type="button"
    className="dynamic-documents-editor__link"
    onClick={onClick}
  >
    {children}
  </button>
);

const DateForms = ({ mapping, payload, onPick }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const COMPONENTS = t(`${NAME_SPACES.COMPONENTS}:TRANSFORMS_EDITOR.OPS.DATE`, {
    returnObjects: true,
  });
  const raw = readSourceValue(mapping, payload);
  const current = dateTransformOf(mapping);
  const currentFormat = isNil(current)
    ? null
    : current.format || DEFAULT_DATE_FORMAT;
  const example = (format, key) =>
    isEmpty(raw) ? get(COMPONENTS, ["OPTIONS", key]) : formatDate(raw, format);
  const cards = [
    { value: null, key: "RAW", example: isEmpty(raw) ? "—" : String(raw) },
    ...DATE_FORMS.map(({ value, key }) => ({
      value,
      key,
      example: example(value, key),
    })),
  ];
  return (
    <div className="dynamic-documents-editor__forms">
      {cards.map(({ value, key, example: text }) => (
        <button
          type="button"
          key={key}
          className={classNames("dynamic-documents-editor__form-card", {
            "dynamic-documents-editor__form-card--selected":
              value === currentFormat,
          })}
          onClick={() => onPick(value)}
        >
          <span className="dynamic-documents-editor__form-name">
            {t(`FIELD_PANEL.FORMS.${key}`)}
          </span>
          <span className="dynamic-documents-editor__form-example">{text}</span>
        </button>
      ))}
    </div>
  );
};

const AnchorBody = ({
  zone,
  mapping,
  proposal,
  payload,
  resultLabel,
  setMapping,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const FIELD_PANEL = t("FIELD_PANEL", { returnObjects: true });
  const [wantsFixed, setWantsFixed] = useState(
    () => isStaticMapping(mapping) && !isEmpty(mapping.staticValue),
  );
  const mode = modeOf(mapping, wantsFixed);
  const field = findFieldForMapping(asObject(mapping));
  const advancedInPlace =
    mode === MODES.FIELD && isNil(field) && hasSource(asObject(mapping));

  const pickInstead = () => {
    setWantsFixed(false);
    setMapping(undefined);
  };
  const fixedText = () => {
    setWantsFixed(true);
    setMapping({ type: MAPPING_TYPES.STATIC, staticValue: "" });
  };

  return (
    <>
      <Step title={FIELD_PANEL.WHICH_INFORMATION}>
        {mode === MODES.FIELD && (
          <>
            <FieldPicker
              selected={field?.path}
              proposal={proposal}
              payload={payload}
              kinds={VALUE_KINDS}
              onPick={(picked) =>
                setMapping(mappingForField(zone, picked, mapping))
              }
            />
            {advancedInPlace && (
              <p className="dynamic-documents-editor__note">
                {FIELD_PANEL.ADVANCED_IN_PLACE}
              </p>
            )}
            <div className="dynamic-documents-editor__links">
              <LinkButton onClick={() => setMapping(combined(mapping))}>
                {FIELD_PANEL.SEVERAL}
              </LinkButton>
              <LinkButton onClick={fixedText}>
                {FIELD_PANEL.FIXED_TEXT}
              </LinkButton>
            </div>
          </>
        )}
        {mode === MODES.FIXED && (
          <>
            <Input
              autoFocus
              placeholder={FIELD_PANEL.FIXED_PLACEHOLDER}
              value={mapping.staticValue ?? ""}
              onChange={(event) =>
                setMapping({ ...mapping, staticValue: event.target.value })
              }
            />
            <div className="dynamic-documents-editor__links">
              <LinkButton onClick={pickInstead}>
                {FIELD_PANEL.PICK_INSTEAD}
              </LinkButton>
            </div>
          </>
        )}
        {mode === MODES.EMPTY && (
          <>
            <p className="dynamic-documents-editor__note">
              {FIELD_PANEL.EMPTY_MESSAGE}
            </p>
            <div className="dynamic-documents-editor__links">
              <LinkButton onClick={pickInstead}>
                {FIELD_PANEL.PICK_INSTEAD}
              </LinkButton>
              <LinkButton onClick={fixedText}>
                {FIELD_PANEL.FIXED_TEXT}
              </LinkButton>
            </div>
          </>
        )}
        {mode === MODES.COMBINE && (
          <>
            <SourceEditor mapping={mapping} onChange={setMapping} />
            <div className="dynamic-documents-editor__links">
              <LinkButton onClick={() => setMapping(single(mapping))}>
                {FIELD_PANEL.SINGLE_INSTEAD}
              </LinkButton>
            </div>
          </>
        )}
      </Step>
      {mode !== MODES.EMPTY &&
        (hasSource(asObject(mapping)) || isStaticMapping(mapping)) && (
          <ResultStep
            mapping={mapping}
            payload={payload}
            label={resultLabel}
            onFormChange={(format) =>
              setMapping(withDateFormat(mapping, format))
            }
          />
        )}
      <AdvancedOptions
        zone={zone}
        mappings={{ [zone.key]: mapping }}
        onChange={(next) => setMapping(next[zone.key])}
        onLeaveEmpty={() => {
          setWantsFixed(false);
          setMapping(LEAVE_EMPTY_MAPPING);
        }}
      />
    </>
  );
};

const ResultStep = ({ mapping, payload, label, onFormChange }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const [formsOpen, setFormsOpen] = useState(false);
  const field = findFieldForMapping(asObject(mapping));
  const transform = dateTransformOf(mapping);
  const isDate = field?.kind === FIELD_KINDS.DATE || !isNil(transform);
  const value = valueText(readMappingValue(mapping, payload), { t });
  const format = isNil(transform)
    ? null
    : transform.format || DEFAULT_DATE_FORMAT;
  const formKey = isNil(format)
    ? "RAW"
    : (find(DATE_FORMS, { value: format })?.key ?? "CUSTOM");
  return (
    <Step>
      <div className="dynamic-documents-editor__result">
        <span>{label}</span>
        <strong
          className={classNames({
            "dynamic-documents-editor__muted": isNil(value),
          })}
        >
          {value ?? t("FIELD_PANEL.RESULT_EMPTY")}
        </strong>
        {isDate && (
          <>
            <span className="dynamic-documents-editor__dot" />
            <span>
              {t("FIELD_PANEL.FORM")} {t(`FIELD_PANEL.FORMS.${formKey}`)}
            </span>
            <span className="dynamic-documents-editor__dot" />
            <LinkButton onClick={() => setFormsOpen((open) => !open)}>
              {t(formsOpen ? "FIELD_PANEL.HIDE_FORMS" : "FIELD_PANEL.CHANGE")}
            </LinkButton>
          </>
        )}
      </div>
      {isDate && formsOpen && (
        <DateForms
          mapping={mapping}
          payload={payload}
          onPick={(next) => {
            setFormsOpen(false);
            onFormChange(next);
          }}
        />
      )}
    </Step>
  );
};

const CheckboxBody = ({ zone, mappings, payload, onChange }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const FIELD_PANEL = t("FIELD_PANEL", { returnObjects: true });
  const [pickedKey, setPickedKey] = useState(null);
  const key = checkboxKey(zone, mappings) ?? pickedKey;
  const field = findFieldByPath(key);
  const isBoolean = field?.kind === FIELD_KINDS.BOOLEAN;
  const booleanOptions = [
    { value: BOOLEAN_VALUES.YES, label: t("VALUES.YES") },
    { value: BOOLEAN_VALUES.NO, label: t("VALUES.NO") },
  ];
  const shownValue = (text) =>
    isBoolean ? (find(booleanOptions, { value: text })?.label ?? text) : text;
  const current = shownValue(ruleText(get(payload, key ?? "")));

  const setVariant = (anchor, next) =>
    onChange(
      isNil(next)
        ? withoutKey(mappings, anchor)
        : { ...mappings, [anchor]: next },
    );

  const pickField = (picked) => {
    setPickedKey(picked.path);
    onChange(
      zone.anchors.reduce((next, anchor) => {
        const mapping = next[anchor];
        const rules = get(mapping, "conditions.rules") || [];
        if (rules.length !== 1 || !isSimpleRule(mapping.conditions, key))
          return next;
        return {
          ...next,
          [anchor]: {
            ...mapping,
            conditions: {
              ...mapping.conditions,
              rules: [{ ...rules[0], key: picked.path }],
            },
          },
        };
      }, mappings),
    );
  };

  const setValue = (anchor, value) => {
    if (isEmpty(value)) {
      setVariant(anchor, undefined);
      return;
    }
    const mapping = mappings[anchor] || {};
    setVariant(anchor, {
      ...mapping,
      conditions: {
        operator:
          get(mapping, "conditions.operator") || CONDITION_OPERATORS.AND,
        rules: [{ key, op: RULE_OPERATORS.EQUALS, value }],
      },
    });
  };

  return (
    <>
      <Step title={FIELD_PANEL.WHICH_INFORMATION}>
        <FieldPicker
          selected={key}
          payload={payload}
          kinds={CHECKBOX_KINDS}
          plainOnly
          onPick={pickField}
        />
        {!isNil(key) && (
          <p className="dynamic-documents-editor__note">
            {t("FIELD_PANEL.CURRENT_VALUE", {
              value: current ?? t("FIELD_PANEL.RESULT_EMPTY"),
            })}
          </p>
        )}
      </Step>
      <Step title={FIELD_PANEL.WHICH_BOX}>
        {zone.anchors.map((anchor) => {
          const exists = !isNil(mappings[anchor]);
          const mapping = mappings[anchor] || {};
          const simple = isSimpleRule(mapping.conditions, key);
          const rules = get(mapping, "conditions.rules") || [];
          const checked = exists && conditionsPass(mapping.conditions, payload);
          const placeholder = boxPlaceholder(
            { key, exists, rules },
            FIELD_PANEL,
          );
          const value = get(rules, "0.value") ?? "";
          const valueInput = () =>
            isBoolean ? (
              <Select
                size="small"
                allowClear
                className="dynamic-documents__field-full"
                placeholder={placeholder}
                value={isEmpty(value) ? undefined : value}
                options={booleanOptions}
                onChange={(next) => setValue(anchor, next ?? "")}
              />
            ) : (
              <Input
                size="small"
                disabled={isNil(key)}
                placeholder={placeholder}
                value={value}
                onChange={(event) => setValue(anchor, event.target.value)}
              />
            );
          return (
            <div key={anchor} className="dynamic-documents-editor__box-row">
              <span className="dynamic-documents-editor__box-name">
                {t("FIELD_PANEL.BOX", { name: anchor })}
              </span>
              {simple ? (
                valueInput()
              ) : (
                <span className="dynamic-documents-editor__muted">
                  {FIELD_PANEL.BOX_ADVANCED}
                </span>
              )}
              {checked && (
                <span className="dynamic-documents-editor__box-checked">
                  {FIELD_PANEL.BOX_CHECKED}
                </span>
              )}
            </div>
          );
        })}
      </Step>
      <AdvancedOptions zone={zone} mappings={mappings} onChange={onChange} />
    </>
  );
};

const ChildLoopBody = ({ zone, mapping, payload, setMapping }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const FIELD_PANEL = t("FIELD_PANEL", { returnObjects: true });
  const lists = fieldOptions({ kinds: LIST_KINDS }).flatMap(
    ({ options }) => options,
  );
  const listPath = listPathOf(mapping);
  const field = findFieldByPath(listPath);
  const autoColumn = tableColumnOf(zone, field);

  const pickList = (picked) =>
    setMapping(
      omit(mappingForField(zone, picked, mapping), [
        ...(picked.path === listPath ? [] : ["itemKey"]),
      ]),
    );

  return (
    <>
      <Step title={FIELD_PANEL.WHICH_LIST}>
        <div className="dynamic-documents-editor__picker-list">
          {lists.map(({ value, field: list }) => (
            <button
              type="button"
              key={value}
              className={classNames("dynamic-documents-editor__picker-option", {
                "dynamic-documents-editor__picker-option--selected":
                  value === listPath,
              })}
              onClick={() => pickList(list)}
            >
              <span className="dynamic-documents-editor__picker-label">
                {fieldPhrase(list)}
              </span>
              <span className="dynamic-documents-editor__picker-sample">
                {valueText(readFieldValue(list, payload), { t })}
              </span>
            </button>
          ))}
        </div>
        {!isEmpty(listPath) && isNil(field) && (
          <p className="dynamic-documents-editor__note">
            {t("FIELD_PANEL.OTHER_LIST", { path: listPath })}
          </p>
        )}
      </Step>
      <Step title={FIELD_PANEL.WHICH_COLUMNS}>
        {isEmpty(listPath) ? (
          <p className="dynamic-documents-editor__muted">
            {FIELD_PANEL.COLUMN_PICK_LIST_FIRST}
          </p>
        ) : (
          <Select
            size="small"
            className="dynamic-documents__field-full"
            value={get(mapping, "itemKey") ?? ""}
            onChange={(itemKey) =>
              setMapping(
                isEmpty(itemKey)
                  ? omit(mapping, ["itemKey"])
                  : { ...mapping, itemKey },
              )
            }
            options={[
              {
                value: "",
                label: isNil(autoColumn)
                  ? t("FIELD_PANEL.COLUMN_AUTO_UNKNOWN")
                  : t("FIELD_PANEL.COLUMN_AUTO", { label: autoColumn.label }),
              },
              ...(field?.items || []).map(({ itemKey, label }) => ({
                value: itemKey,
                label,
              })),
            ]}
          />
        )}
      </Step>
      <AdvancedOptions
        zone={zone}
        mappings={{ [zone.key]: mapping }}
        onChange={(next) => setMapping(next[zone.key])}
      />
    </>
  );
};

const FieldPanel = ({
  zone,
  mappings,
  proposal,
  payload,
  resultLabel,
  canSave,
  onChange,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const FIELD_PANEL = t("FIELD_PANEL", { returnObjects: true });
  const setMapping = (next) =>
    onChange(
      isNil(next)
        ? withoutKey(mappings, zone.key)
        : { ...mappings, [zone.key]: next },
    );

  const BODIES = {
    [ZONE_TYPES.ANCHOR]: () => (
      <AnchorBody
        zone={zone}
        mapping={mappings[zone.key]}
        proposal={proposal}
        payload={payload}
        resultLabel={resultLabel}
        setMapping={setMapping}
      />
    ),
    [ZONE_TYPES.CHECKBOX_GROUP]: () => (
      <CheckboxBody
        zone={zone}
        mappings={mappings}
        payload={payload}
        onChange={onChange}
      />
    ),
    [ZONE_TYPES.CHILD_LOOP]: () => (
      <ChildLoopBody
        zone={zone}
        mapping={mappings[zone.key]}
        payload={payload}
        setMapping={setMapping}
      />
    ),
    [ZONE_TYPES.SIGNATURE]: () => (
      <p className="dynamic-documents-editor__note">{FIELD_PANEL.SIGNATURE}</p>
    ),
  };

  return (
    <div
      className="dynamic-documents-editor__panel"
      aria-label={zoneLabel(zone, t)}
    >
      {BODIES[zone.type]()}
      <div className="dynamic-documents-editor__panel-footer">
        <button
          type="button"
          className="dynamic-documents-editor__link"
          onClick={onClose}
        >
          {FIELD_PANEL.CLOSE}
        </button>
        {zone.type !== ZONE_TYPES.SIGNATURE && (
          <button
            type="button"
            className="dynamic-documents-editor__btn dynamic-documents-editor__btn--sm dynamic-documents-editor__btn--primary"
            disabled={!canSave}
            onClick={onConfirm}
          >
            {FIELD_PANEL.CONFIRM}
          </button>
        )}
      </div>
    </div>
  );
};

export default FieldPanel;
